import { decodeJwt, decodeProtectedHeader, importJWK, jwtVerify, } from 'jose';
import { authorizationSchema } from '../types/auth.js';
export const decodeJwtPayloadUnverified = (token) => decodeJwt(token);
/**
 * Reads `scopes` from a JWT by validating the payload as {@link Authorization} (no signature verification).
 * Session and access tokens embed that shape in the claim set.
 */
export const parseScopesFromJwt = (token) => {
    if (!(token === null || token === void 0 ? void 0 : token.trim())) {
        return [];
    }
    try {
        const parsed = authorizationSchema.safeParse(decodeJwt(token));
        return parsed.success ? parsed.data.scopes : [];
    }
    catch {
        return [];
    }
};
/**
 * True for JWTs that authenticate a service identity without a profile
 * (e.g. jam observer / egress links). These cannot load `profiles/current`.
 */
export const isServiceOnlyJwt = (token) => {
    if (!(token === null || token === void 0 ? void 0 : token.trim())) {
        return false;
    }
    try {
        const parsed = authorizationSchema.safeParse(decodeJwt(token));
        if (!parsed.success)
            return false;
        const { service, profile } = parsed.data.identities;
        return !!service && !profile;
    }
    catch {
        return false;
    }
};
/**
 * True for guest-pass JWTs: a profile identity with no account (1d TTL).
 * These must not use the login JWT refresh path or `/accounts/current`.
 */
export const isAccountlessJwt = (token) => {
    if (!(token === null || token === void 0 ? void 0 : token.trim())) {
        return false;
    }
    try {
        const parsed = authorizationSchema.safeParse(decodeJwt(token));
        if (!parsed.success)
            return false;
        const { profile, account, service } = parsed.data.identities;
        return !!profile && !account && !service;
    }
    catch {
        return false;
    }
};
/**
 * True when the token has a numeric `exp`, is not expired yet, and seconds until expiry is at least
 * `minRemainingSeconds`. Non-finite or negative `minRemainingSeconds` yields false. Invalid or undecodable
 * tokens return false.
 */
export const jwtHasRemainingValidityAtLeast = (token, minRemainingSeconds) => {
    if (!Number.isFinite(minRemainingSeconds) ||
        minRemainingSeconds < 0 ||
        !(token === null || token === void 0 ? void 0 : token.trim())) {
        return false;
    }
    try {
        const { exp } = decodeJwt(token);
        if (typeof exp !== 'number') {
            return false;
        }
        const nowSec = Date.now() / 1000;
        return exp - nowSec >= minRemainingSeconds;
    }
    catch {
        return false;
    }
};
export const verifyJwt = async (token, publicKey) => {
    const jwkPublicKey = await importJWK({
        kty: 'OKP',
        crv: 'Ed25519',
        x: publicKey,
    });
    const verification = await jwtVerify(token, jwkPublicKey, {
        algorithms: ['EdDSA'],
    }).catch(() => undefined);
    return verification === null || verification === void 0 ? void 0 : verification.payload;
};
export const parseJwtKeyId = (token) => {
    try {
        const { kid } = decodeProtectedHeader(token);
        return typeof kid === 'string' ? kid : undefined;
    }
    catch {
        return undefined;
    }
};
